/* Ronini parallax media (Coterie "parallaxMedia" pattern): large photos drift slightly slower than the page.
   The image is scaled ~1.1 inside a clipping frame and translated opposite to the scroll while the frame crosses the
   viewport (max ±70px, never beyond the scaled margin, so no gaps). Uses the individual `scale`/`translate`
   properties so existing transforms/fades on the image keep working. Only visible images are computed each frame.
   Targets: large images in <main> (≥600px wide on desktop, ≥85% of the viewport on phones, ≥220px tall), plus
   anything marked [data-rn-parallax]. Skips cards, carousels' small media, galleries, the scroll-scrubbed bottle,
   and anything under [data-no-parallax]. Off for prefers-reduced-motion and in the theme editor. */
(() => {
  if (window.rnParallax) return;
  window.rnParallax = 1;
  if (window.Shopify && window.Shopify.designMode) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  // 10-07: no parallax on touch devices — the per-scroll layout reads were part of the phone jank; images stay still.
  if (matchMedia('(hover: none) and (pointer: coarse)').matches) return;
  if (reduce.matches) return;

  const SCALE = 1.1;
  const MAX_SHIFT = 70;
  const EXCLUDE = [
    '[data-no-parallax]', '[id*="scrolling_image_cards"]', '.rn-sc', '.sticky-scene',
    '[class*="card"]', '.product-gallery', '[class*="gallery"]', '[class*="buybox"]', '.ronini-bb-wrap',
    '.shopify-section--header', '.shopify-section-group-header-group', '.shopify-section-group-footer-group', 'dialog', 'x-modal', 'x-drawer', '[class*="swipe-row"]', '.rn-swipe', '[class*="rn-swipe"]', '[class*="thumb"]',
    '.hero-next', '[class*="logo"]', '[class*="badge"]', '[class*="icon"]',
  ].join(',');

  const items = new Map(); // img -> { frame, shift }
  const visible = new Set();

  const approx = (a, b) => Math.abs(a.top - b.top) < 3 && Math.abs(a.bottom - b.bottom) < 3 && Math.abs(a.left - b.left) < 3 && Math.abs(a.right - b.right) < 3;

  const frameFor = (img) => {
    const r = img.getBoundingClientRect();
    let frame = null;
    let el = img.parentElement;
    for (let i = 0; i < 4 && el && el !== document.body; i++, el = el.parentElement) {
      const cs = getComputedStyle(el);
      if (cs.display === 'contents' || cs.display === 'inline') continue;
      if (approx(el.getBoundingClientRect(), r)) frame = el; else break;
    }
    return frame;
  };

  // Images inside a horizontally scrolling row (swipe rows, carousels) are card media — leave them alone.
  const inHScroller = (el) => {
    for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
      const ox = getComputedStyle(a).overflowX;
      if ((ox === 'auto' || ox === 'scroll') && a.scrollWidth > a.clientWidth + 4) return true;
      if (a.classList.contains('swiper-wrapper') && a.children.length > 1 && !a.closest('[id*="hero"]')) return true;
    }
    return false;
  };

  const qualifies = (img) => {
    if (img.closest(EXCLUDE) && !img.hasAttribute('data-rn-parallax')) return false;
    if (!img.hasAttribute('data-rn-parallax') && inHScroller(img)) return false;
    const r = img.getBoundingClientRect();
    if (img.hasAttribute('data-rn-parallax')) return r.width > 0 && r.height > 0;
    const vw = window.innerWidth;
    const minW = vw < 768 ? vw * 0.85 : 600;
    return r.width >= minW && r.height >= 220;
  };

  const io = new IntersectionObserver((entries) => {
    for (const e of entries) { if (e.isIntersecting) visible.add(e.target); else visible.delete(e.target); }
    request();
  }, { rootMargin: '10% 0px' });

  const setup = () => {
    const main = document.querySelector('main');
    if (!main) return;
    // Re-check existing ones: a swipe row may only become scrollable after its script runs.
    for (const [img, it] of items) {
      if (!img.hasAttribute('data-rn-parallax') && (img.closest(EXCLUDE) || inHScroller(img))) {
        img.style.scale = ''; img.style.translate = ''; img.style.willChange = '';
        io.unobserve(img); visible.delete(img); items.delete(img);
      }
    }
    for (const img of main.querySelectorAll('img, video, [data-rn-parallax]')) {
      if (items.has(img)) continue;
      if (!qualifies(img)) continue;
      const frame = frameFor(img);
      if (!frame) continue;
      const fcs = getComputedStyle(frame);
      if (fcs.overflow === 'visible' || fcs.overflowY === 'visible') frame.style.overflow = 'clip';
      img.style.scale = String(SCALE);
      img.style.willChange = 'translate';
      items.set(img, { frame });
      io.observe(img);
    }
    request();
  };

  let queued = false;
  const update = () => {
    queued = false;
    const vh = window.innerHeight;
    for (const img of visible) {
      const it = items.get(img);
      if (!it) continue;
      const r = it.frame.getBoundingClientRect();
      // -1 when the frame's top enters at the bottom of the screen, +1 when its bottom leaves at the top.
      const p = Math.max(-1, Math.min(1, ((vh - r.top) / (vh + r.height)) * 2 - 1));
      const room = ((SCALE - 1) / 2) * r.height; // the scaled margin available on each side
      const shift = Math.min(MAX_SHIFT, room * 0.95);
      img.style.translate = `0 ${(p * shift).toFixed(2)}px`;
    }
  };
  const request = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };

  if (window.rnLenis && window.rnLenis.on) window.rnLenis.on('scroll', request);
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', () => { request(); }, { passive: true });
  reduce.addEventListener?.('change', (e) => { if (e.matches) for (const img of items.keys()) { img.style.scale = ''; img.style.translate = ''; } });

  setup();
  window.addEventListener('load', setup, { once: true });
  // Lazy / late images (tabs, sliders) — re-scan occasionally when layout changes.
  // A couple of delayed passes for late images — no per-scroll/resize rescans (those cost layout during scrolling).
  setTimeout(setup, 1500); setTimeout(setup, 4000);
})();
